import { authenticatedFetch } from "./apiClient";
import { parseApiErrorBody } from "../utils/parseApiError";
import { throwIfNotOk } from "../utils/apiRequestError";
import { rateLimitToastMessage, parseRetryAfterSeconds } from "../utils/httpRateLimit";
import { CurrentUserResponse, normalizeCurrentUserResponse } from "../types/user";

function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim());
    } catch {
      return utf8Match[1].trim();
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(header);
  if (quoted?.[1]) return quoted[1];
  const plain = /filename=([^;]+)/i.exec(header);
  return plain?.[1]?.trim() ?? null;
}

export type DataExportResult = {
  blob: Blob;
  filename: string;
};

/**
 * GET /api/me — backend user profile flags (admin is set in the database, not Clerk).
 */
export async function fetchCurrentUser(): Promise<CurrentUserResponse> {
  const response = await authenticatedFetch("/me");

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("Session expired. Please sign in again.");
    }
    if (response.status === 404) {
      return { is_admin: false };
    }
    if (response.status === 429) {
      const retryAfter = parseRetryAfterSeconds(response);
      throw new Error(rateLimitToastMessage(retryAfter));
    }
    const error = await response.json().catch(() => ({}));
    const message = parseApiErrorBody(
      error && typeof error === "object" ? error : {},
      `Failed to load user profile (${response.status}).`
    ).message;
    throw new Error(message);
  }

  const raw = await response.json().catch(() => ({}));
  return normalizeCurrentUserResponse(raw);
}

export async function fetchMyDataExport(): Promise<DataExportResult> {
  const response = await authenticatedFetch("/me/data-export", { method: "GET" });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("Session expired. Please sign in again.");
    }
    if (response.status === 429) {
      const retryAfter = parseRetryAfterSeconds(response);
      throw new Error(rateLimitToastMessage(retryAfter));
    }
    const detail = await response.text().catch(() => "");
    throw new Error(detail || `Failed to export data (${response.status}).`);
  }

  const blob = await response.blob();
  const filename =
    filenameFromContentDisposition(response.headers.get("Content-Disposition")) ??
    `tessera-data-export-${new Date().toISOString().slice(0, 10)}.xlsx`;

  return { blob, filename };
}
